/*
  Product card behaviour.

  <fable-card>        swatch selection, add to cart, opening the options popup
  window.FableQuickAdd  one shared <dialog class="fable-quick-add"> that asks for the
                        options a card cannot express with its swatches

  No dependency on product-form.js. Cart flow: POST to routes.cart_add_url with the
  sections of the cart drawer (or notification), then cart.renderContents().
*/
(function () {
  if (customElements.get('fable-card')) return;

  const productRequests = new Map();

  /* Product JSON (options, variants) by product URL; one request per product. */
  function fetchProduct(productUrl) {
    const match = String(productUrl || '').match(/\/products\/([^/?#]+)/);
    if (!match) return Promise.reject(new Error('No product handle in ' + productUrl));
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = root + 'products/' + match[1] + '.js';
    if (!productRequests.has(url)) {
      productRequests.set(
        url,
        fetch(url, { headers: { Accept: 'application/json' } })
          .then((response) => {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.json();
          })
          .catch((error) => {
            productRequests.delete(url);
            throw error;
          })
      );
    }
    return productRequests.get(url);
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* ------------------------------------------------------------------------
     Options popup
     ------------------------------------------------------------------------ */

  const FableQuickAdd = {
    dialog: null,
    card: null,
    trigger: null,
    product: null,
    fixedIndex: -1,
    fixedValue: null,
    locked: false,

    ensure() {
      if (this.dialog && this.dialog.isConnected) return this.dialog;
      const dialog = element('dialog', 'fable-quick-add');
      dialog.setAttribute('aria-labelledby', 'FableQuickAddTitle');
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.closest('.fable-quick-add__close')) this.close();
      });
      dialog.addEventListener('change', (event) => {
        if (event.target.matches('select')) this.update();
      });
      /* fires for close(), Escape and the backdrop alike */
      dialog.addEventListener('close', () => {
        const trigger = this.trigger;
        const card = this.card;
        this.trigger = null;
        this.card = null;
        this.product = null;
        this.unlock();
        dialog.textContent = '';
        if (!trigger || !trigger.isConnected) return;
        trigger.focus();
        if (document.activeElement !== trigger && card) {
          const target = card.focusTarget(trigger);
          if (target) target.focus();
        }
      });
      document.body.appendChild(dialog);
      this.dialog = dialog;
      return dialog;
    },

    /* page scroll is locked while the popup is open, like every other overlay */
    lock() {
      if (this.locked || document.body.classList.contains('overflow-hidden')) return;
      document.body.classList.add('overflow-hidden');
      this.locked = true;
    },

    unlock() {
      if (!this.locked) return;
      document.body.classList.remove('overflow-hidden');
      this.locked = false;
    },

    close() {
      this.unlock();
      if (this.dialog && this.dialog.open) this.dialog.close();
    },

    open(card, trigger) {
      if (trigger.getAttribute('aria-disabled') === 'true') return Promise.resolve();
      trigger.setAttribute('aria-disabled', 'true');
      return fetchProduct(card.dataset.productUrl)
        .then((product) => {
          trigger.removeAttribute('aria-disabled');
          this.render(card, trigger, product);
        })
        .catch(() => {
          /* no product data: the product page can always take over */
          trigger.removeAttribute('aria-disabled');
          window.location.href = card.productUrl;
        });
    },

    render(card, trigger, product) {
      const dialog = this.ensure();
      if (dialog.open) return;
      if (typeof dialog.showModal !== 'function') {
        window.location.href = card.productUrl;
        return;
      }
      this.card = card;
      this.trigger = trigger;
      this.product = product;

      const variants = product.variants || [];
      const current =
        variants.find((variant) => String(variant.id) === String(card.dataset.variantId)) ||
        variants.find((variant) => variant.available) ||
        variants[0];

      /* the card's swatch option is already chosen on the tile */
      const swatchPosition = parseInt(card.dataset.swatchOption, 10);
      const pressed = card.querySelector('.fable-card__swatch[aria-pressed="true"]');
      this.fixedIndex = pressed && swatchPosition ? swatchPosition - 1 : -1;
      this.fixedValue = this.fixedIndex > -1 ? pressed.dataset.value : null;

      const panel = element('div', 'fable-quick-add__panel');
      const title = element('h2', 'visually-hidden', card.dataset.productTitle || product.title);
      title.id = 'FableQuickAddTitle';
      panel.appendChild(title);

      (product.options || []).forEach((option, index) => {
        if (index === this.fixedIndex) return;
        const id = 'FableQuickAdd-Option-' + index;
        const label = element('label', 'fable-quick-add__label', (card.dataset.labelSelect + ' ' + option.name).trim());
        label.htmlFor = id;
        const select = element('select', 'fable-quick-add__select');
        select.id = id;
        select.dataset.index = index;
        (option.values || []).forEach((value) => {
          const item = element('option', '', value);
          item.value = value;
          item.dataset.label = value;
          if (current && current.options[index] === value) item.selected = true;
          select.appendChild(item);
        });
        panel.appendChild(label);
        panel.appendChild(select);
      });

      const error = element('p', 'fable-quick-add__error');
      error.setAttribute('role', 'alert');
      error.hidden = true;
      panel.appendChild(error);

      const buttons = element('div', 'fable-quick-add__buttons');
      const add = element('button', 'fable-card__btn fable-card__btn--primary');
      add.type = 'button';
      add.appendChild(element('span', '', card.dataset.labelAdd));
      add.addEventListener('click', () => this.submit());
      const details = element('a', 'fable-card__btn fable-card__btn--secondary', card.dataset.labelDetails);
      details.href = card.productUrl;
      buttons.appendChild(add);
      buttons.appendChild(details);
      panel.appendChild(buttons);

      const close = element('button', 'fable-quick-add__close');
      close.type = 'button';
      close.setAttribute('aria-label', card.dataset.labelClose || 'Close');
      close.innerHTML =
        '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M1 1l10 10M11 1L1 11"/></svg>';
      panel.appendChild(close);

      dialog.textContent = '';
      dialog.appendChild(panel);
      this.update();
      this.lock();
      dialog.showModal();
    },

    selection() {
      const values = [];
      (this.product.options || []).forEach((option, index) => {
        if (index === this.fixedIndex) {
          values[index] = this.fixedValue;
          return;
        }
        const select = this.dialog.querySelector('select[data-index="' + index + '"]');
        values[index] = select ? select.value : null;
      });
      return values;
    },

    findVariant(values) {
      return (this.product.variants || []).find((variant) => variant.options.every((value, index) => value === values[index]));
    },

    /* Reflect availability in the option lists and in the add button. */
    update() {
      if (!this.product || !this.card) return;
      const card = this.card;
      const values = this.selection();

      this.dialog.querySelectorAll('select').forEach((select) => {
        const index = parseInt(select.dataset.index, 10);
        Array.from(select.options).forEach((item) => {
          const probe = values.slice();
          probe[index] = item.value;
          const variant = this.findVariant(probe);
          let suffix = '';
          if (!variant) suffix = ' (' + card.dataset.labelUnavailable + ')';
          else if (!variant.available) suffix = ' (' + card.dataset.labelSoldOut + ')';
          item.textContent = item.dataset.label + suffix;
        });
      });

      const variant = this.findVariant(values);
      const add = this.dialog.querySelector('.fable-quick-add__buttons button');
      const details = this.dialog.querySelector('.fable-quick-add__buttons a');
      const error = this.dialog.querySelector('.fable-quick-add__error');
      if (error) error.hidden = true;

      let label = card.dataset.labelAdd;
      if (!variant) label = card.dataset.labelUnavailable;
      else if (!variant.available) label = card.dataset.labelSoldOut;
      const blocked = !variant || !variant.available;
      add.disabled = blocked;
      add.classList.toggle('fable-card__btn--primary', !blocked);
      add.classList.toggle('fable-card__btn--disabled', blocked);
      add.firstElementChild.textContent = label;
      add.dataset.variantId = variant ? variant.id : '';
      details.href = variant ? card.productUrl + (card.productUrl.indexOf('?') > -1 ? '&' : '?') + 'variant=' + variant.id : card.productUrl;
    },

    submit() {
      const card = this.card;
      const add = this.dialog.querySelector('.fable-quick-add__buttons button');
      const error = this.dialog.querySelector('.fable-quick-add__error');
      if (!card || !add || !add.dataset.variantId) return;
      error.hidden = true;
      card
        .add(add.dataset.variantId, add, {
          focusAfter: this.trigger,
          beforeRender: () => this.close(),
        })
        .then((result) => {
          if (result && result.error && this.dialog.open) {
            error.textContent = result.error;
            error.hidden = false;
          }
        });
    },
  };

  window.FableQuickAdd = FableQuickAdd;

  /* ------------------------------------------------------------------------
     Card
     ------------------------------------------------------------------------ */

  class FableCard extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onSwatchIntent = this.onSwatchIntent.bind(this);
      this.swatchLoad = 0;
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      /* a colour's photo starts downloading when a swatch is pointed at, focused or touched */
      this.addEventListener('pointerover', this.onSwatchIntent);
      this.addEventListener('focusin', this.onSwatchIntent);
      this.addEventListener('touchstart', this.onSwatchIntent, { passive: true });
      /* the hover image layer stays out of the layout, so its lazy image is not requested, until the
         pointer or the keyboard first reaches the card (fable-card.css, after the hover rules) */
      const arm = () => this.classList.add('is-armed');
      this.addEventListener('pointerenter', arm, { once: true });
      this.addEventListener('focusin', arm, { once: true });
      /* safety net for the CSS fade-in: images that finished before their inline onload could run */
      this.querySelectorAll('img.fable-card__img:not(.is-loaded)').forEach((image) => {
        const reveal = () => image.classList.add('is-loaded');
        if (image.complete) reveal();
        else {
          image.addEventListener('load', reveal, { once: true });
          image.addEventListener('error', reveal, { once: true });
        }
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerover', this.onSwatchIntent);
      this.removeEventListener('focusin', this.onSwatchIntent);
      this.removeEventListener('touchstart', this.onSwatchIntent);
    }

    /* Fetch a swatch's photo ahead of the click, once, with the card image's sizes, so the browser
       picks the same file the swap will ask for and the swap is served from the cache. */
    onSwatchIntent(event) {
      const swatch = event.target.closest && event.target.closest('.fable-card__swatch');
      if (!swatch || !this.contains(swatch) || swatch.dataset.preloaded || !swatch.dataset.imageSrc) return;
      swatch.dataset.preloaded = 'true';
      const card = this.querySelector('.fable-card__media--primary img');
      const preload = new Image();
      if (swatch.dataset.imageSrcset) {
        preload.sizes = (card && card.getAttribute('sizes')) || '100vw';
        preload.srcset = swatch.dataset.imageSrcset;
      }
      preload.src = swatch.dataset.imageSrc;
    }

    /*
      Where focus goes back to after the cart or the popup closes. The hover buttons are
      hidden while the tile is at rest (unlike the swatches), so the tile link stands in for them.
    */
    focusTarget(control) {
      const link = this.querySelector('.fable-card__link');
      if (link && control && control.closest('.fable-card__actions')) return link;
      return control;
    }

    /* product URL without a variant parameter */
    get productUrl() {
      return (this.dataset.productUrl || '').split('?')[0];
    }

    onClick(event) {
      const swatch = event.target.closest('.fable-card__swatch');
      if (swatch && this.contains(swatch)) {
        event.preventDefault();
        this.selectSwatch(swatch);
        return;
      }
      const trigger = event.target.closest('[data-fable-add]');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        if (trigger.dataset.fableAdd === 'options') FableQuickAdd.open(this, trigger);
        else this.add(this.dataset.variantId, trigger);
      }
    }

    selectSwatch(swatch) {
      this.querySelectorAll('.fable-card__swatch').forEach((item) => {
        item.setAttribute('aria-pressed', item === swatch ? 'true' : 'false');
      });
      if (swatch.dataset.variantId) this.dataset.variantId = swatch.dataset.variantId;
      if (swatch.dataset.variantUrl) {
        this.querySelectorAll('a.fable-card__link, a.fable-card__btn--primary, a.fable-card__btn--secondary').forEach((link) => {
          link.setAttribute('href', swatch.dataset.variantUrl);
        });
      }
      if (swatch.dataset.imageSrc) {
        const image = this.querySelector('.fable-card__media--primary img');
        if (image) {
          image.classList.add('is-loaded');
          if (swatch.dataset.imageSrcset) image.srcset = swatch.dataset.imageSrcset;
          else image.removeAttribute('srcset');
          image.src = swatch.dataset.imageSrc;
          /* fill image style: the media box takes the new image's aspect ratio (fable-card.css, section 5) */
          if (swatch.dataset.imageRatio && image.parentElement) {
            image.parentElement.style.setProperty('--fable-card-ratio', swatch.dataset.imageRatio);
          }
          this.classList.add('is-swatched');
          this.watchSwatchImage(image);
        }
      }
      this.clearError();
      this.setSoldOut(swatch.dataset.available === 'false');
    }

    /*
      While a colour's photo downloads the previous one stays on screen: the media box dims it and
      shows a spinner (fable-card.css, section 3), from 150ms on so a quick load never flashes. Each
      pick takes a number; only the latest pick's load or error ends the state.
    */
    watchSwatchImage(image) {
      const box = image.closest('.fable-card__media--primary');
      const link = this.querySelector('.fable-card__link');
      const ticket = ++this.swatchLoad;
      clearTimeout(this.swatchLoadTimer);
      const done = () => {
        if (ticket !== this.swatchLoad) return;
        clearTimeout(this.swatchLoadTimer);
        if (box) box.classList.remove('is-loading');
        if (link) link.removeAttribute('aria-busy');
      };
      if (image.complete && image.naturalWidth) {
        done();
        return;
      }
      this.swatchLoadTimer = setTimeout(() => {
        if (ticket !== this.swatchLoad || (image.complete && image.naturalWidth)) return;
        if (box) box.classList.add('is-loading');
        if (link) link.setAttribute('aria-busy', 'true');
      }, 150);
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
    }

    setSoldOut(soldOut) {
      const trigger = this.querySelector('[data-fable-add]');
      if (!trigger) return;
      const filled = !trigger.classList.contains('fable-card__btn--outline');
      trigger.disabled = soldOut;
      trigger.classList.toggle('fable-card__btn--disabled', soldOut);
      if (filled) trigger.classList.toggle('fable-card__btn--primary', !soldOut);
      const label = trigger.querySelector('span') || trigger;
      if (!trigger.dataset.label) trigger.dataset.label = label.textContent;
      label.textContent = soldOut ? this.dataset.labelSoldOut : trigger.dataset.label;
    }

    showError(message) {
      let node = this.querySelector('.fable-card__error');
      if (!node) {
        node = element('p', 'fable-card__error');
        node.setAttribute('role', 'alert');
        /* in the text block, above the price: the buttons lie over the title and take no room of their own */
        const anchor = this.querySelector('.fable-card__priceline, .fable-card__swatches');
        if (anchor) anchor.before(node);
        else this.appendChild(node);
      }
      node.textContent = message;
      node.hidden = false;
    }

    clearError() {
      const node = this.querySelector('.fable-card__error');
      if (node) node.hidden = true;
    }

    /*
      Adds one unit of a variant. Resolves with { error } when the cart refuses it.
      options.focusAfter   element that gets focus back when the cart closes (default: trigger)
      options.beforeRender called after a successful add, before the cart opens
    */
    add(variantId, trigger, options) {
      const settings = options || {};
      if (!variantId || !trigger || trigger.getAttribute('aria-disabled') === 'true') return Promise.resolve({});
      const inPopup = Boolean(trigger.closest('.fable-quick-add'));
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const routes = window.routes || {};

      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        body.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(this.focusTarget(settings.focusAfter || trigger));
      }

      this.clearError();
      trigger.setAttribute('aria-disabled', 'true');

      return fetch(routes.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: body,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            const message = response.description || response.message || this.dataset.labelUnavailable;
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'fable-card',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
            }
            if (!inPopup) this.showError(message);
            return { error: message };
          }

          if (!cart) {
            window.location = routes.cart_url || '/cart';
            return {};
          }

          if (typeof settings.beforeRender === 'function') settings.beforeRender();
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'fable-card',
              productVariantId: variantId,
              cartData: response,
            });
          }
          cart.classList.remove('is-empty');
          cart.renderContents(response);
          return {};
        })
        .catch((error) => {
          console.error(error);
          const message = (window.cartStrings && window.cartStrings.error) || this.dataset.labelUnavailable;
          if (!inPopup) this.showError(message);
          return { error: message };
        })
        .finally(() => {
          trigger.removeAttribute('aria-disabled');
        });
    }
  }

  customElements.define('fable-card', FableCard);
})();
